import {
  ASSET_STATUSES,
  ASSET_TYPES,
  AssetStatusTag,
  createdAtFilter,
  showNumber,
  toFilterOptions,
} from "../../../../hooks/assets/assetUtils";
import { ApprovalStatusTag } from "../../../common/registerUtils";
import {
  API_ASSET_STATUS_UPDATES_REPORT_EXPORT,
  API_ASSET_STOCK_VARIANCE_REPORT_EXPORT,
  API_ASSET_TRANSFERS_REPORT_EXPORT,
  API_ASSETS_REPORT_EXPORT,
} from "../../../../utils/apis";
import { formatDate } from "../../../../utils/helpers";
import { reportExport } from "./reportUtils";

const statusKey = (label: string) =>
  Object.keys(ASSET_STATUSES).find(
    (key) => ASSET_STATUSES[key as keyof typeof ASSET_STATUSES] === label,
  ) ?? label;

export const useAssetsReport = () => ({
  tableTitle: "Asset Register Report",
  tableColumns: () => [
    { id: "asset_id", header: "Asset ID" },
    { id: "name", header: "Name", filter: { type: "text" } },
    { id: "category", header: "Category", filter: { type: "text" } },
    {
      id: "asset_type",
      header: "Asset Type",
      filter: { type: "select", options: toFilterOptions(ASSET_TYPES) },
    },
    { id: "serial_number", header: "Serial Number" },
    { id: "condition", header: "Condition" },
    {
      id: "status",
      header: "Status",
      filter: { type: "select", options: toFilterOptions(ASSET_STATUSES) },
    },
    { id: "quantity", header: "Quantity" },
    { id: "location", header: "Location", filter: { type: "text" } },
    { id: "assigned_to", header: "Assigned To" },
    { id: "registered_by", header: "Registered By" },
    {
      id: "registration_date",
      header: "Registered On",
      filter: createdAtFilter,
    },
  ],
  tableData: (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      status: <AssetStatusTag status={statusKey(item?.status)} />,
      registration_date: formatDate(item?.registration_date),
    })),
  exportData: reportExport(API_ASSETS_REPORT_EXPORT, "asset_register_report"),
});

export const useAssetTransfersReport = () => ({
  tableTitle: "Asset Transfers Report",
  tableColumns: () => [
    { id: "transfer_id", header: "Reference" },
    { id: "asset_id", header: "Asset ID" },
    {
      id: "asset_name",
      header: "Asset",
      filter: { type: "text", key: "asset" },
    },
    { id: "from_location", header: "From" },
    { id: "to_location", header: "To" },
    { id: "transfer_date", header: "Transfer Date", filter: createdAtFilter },
    { id: "status", header: "Status" },
    { id: "requested_by", header: "Requested By" },
    { id: "approved_by", header: "Approved By" },
  ],
  tableData: (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      status: <ApprovalStatusTag status={item?.status} />,
    })),
  exportData: reportExport(
    API_ASSET_TRANSFERS_REPORT_EXPORT,
    "asset_transfers_report",
  ),
});

export const useAssetStatusUpdatesReport = () => ({
  tableTitle: "Asset Status Updates Report",
  tableColumns: () => [
    { id: "status_update_id", header: "Reference" },
    { id: "asset_id", header: "Asset ID" },
    {
      id: "asset_name",
      header: "Asset",
      filter: { type: "text", key: "asset" },
    },
    { id: "previous_status", header: "Previous Status" },
    {
      id: "new_status",
      header: "New Status",
      filter: { type: "select", options: toFilterOptions(ASSET_STATUSES) },
    },
    { id: "reason", header: "Reason" },
    { id: "effective_date", header: "Effective Date", filter: createdAtFilter },
    { id: "updated_by", header: "Updated By" },
  ],
  tableData: (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      previous_status: (
        <AssetStatusTag status={statusKey(item?.previous_status)} />
      ),
      new_status: <AssetStatusTag status={statusKey(item?.new_status)} />,
    })),
  exportData: reportExport(
    API_ASSET_STATUS_UPDATES_REPORT_EXPORT,
    "asset_status_updates_report",
  ),
});

export const useAssetStockVarianceReport = () => ({
  tableTitle: "Asset Stock Variance Report",
  tableColumns: () => [
    { id: "count_id", header: "Reference" },
    { id: "asset_id", header: "Asset ID" },
    {
      id: "asset_name",
      header: "Asset",
      filter: { type: "text", key: "asset" },
    },
    { id: "location", header: "Location", filter: { type: "text" } },
    { id: "system_quantity", header: "System Quantity" },
    { id: "physical_quantity", header: "Physical Quantity" },
    { id: "variance", header: "Variance" },
    { id: "reason_for_variance", header: "Reason for Variance" },
    { id: "status", header: "Status" },
    { id: "counted_by", header: "Counted By" },
  ],
  tableData: (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      system_quantity: showNumber(item?.system_quantity),
      physical_quantity: showNumber(item?.physical_quantity),
      variance: showNumber(item?.variance),
      status: <ApprovalStatusTag status={item?.status} />,
    })),
  exportData: reportExport(
    API_ASSET_STOCK_VARIANCE_REPORT_EXPORT,
    "asset_stock_variance_report",
  ),
});
